import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import Toast, { ToastType } from '../components/Toast';
import { apiService } from '../services/api';
import { FiSave } from 'react-icons/fi';

interface AppSettingsData {
  id: number;
  contact_title: string;
  contact_address: string;
  contact_zip: string;
  contact_city: string;
  contact_country: string;
  contact_vat: string;
  contact_vat_percent: number;
  contact_invoice_footer_en: string;
  contact_invoice_footer_fr: string;
  updated_at: string;
  [key: string]: any;
}

const ContactBilling: React.FC = () => {
  const [settings, setSettings] = useState<AppSettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await apiService.getAppSettings();
      if (response.success && response.data) {
        setSettings(response.data);
      }
    } catch (error) {
      setToast({ message: 'Failed to load settings', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field: keyof AppSettingsData, value: any) => {
    if (settings) {
      setSettings({ ...settings, [field]: value });
    }
  };

  const handleSave = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const response = await apiService.updateAppSettings({
        contact_title: settings.contact_title,
        contact_address: settings.contact_address,
        contact_zip: settings.contact_zip,
        contact_city: settings.contact_city,
        contact_country: settings.contact_country,
        contact_vat: settings.contact_vat,
        contact_vat_percent: settings.contact_vat_percent,
        contact_invoice_footer_en: settings.contact_invoice_footer_en,
        contact_invoice_footer_fr: settings.contact_invoice_footer_fr,
      });
      if (response.success) {
        setToast({ message: 'Contact / Billing settings saved successfully', type: 'success' });
        if (response.data) {
          setSettings(response.data);
        }
      } else {
        const err = response as any;
        setToast({ message: err.error?.message || 'Failed to save settings', type: 'error' });
      }
    } catch (error: any) {
      setToast({ message: error.message || 'Failed to save settings', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex h-64 items-center justify-center">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-brand-500 border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  if (!settings) {
    return (
      <Layout>
        <div className="flex h-64 items-center justify-center">
          <p className="text-gray-500 dark:text-gray-400">Failed to load settings</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Contact / Billing</h1>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 rounded-lg bg-brand-500 px-6 py-2.5 font-medium text-white hover:bg-brand-600 disabled:opacity-50"
          >
            <FiSave className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>

        <div className="rounded-xl bg-white p-6 shadow-lg dark:bg-navy-800">
          <div className="space-y-6">
            {/* Company Info */}
            <div className="rounded-xl border border-gray-200 p-5 dark:border-gray-700">
              <div className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Company / Organization Title</label>
                  <input
                    type="text"
                    value={settings.contact_title}
                    onChange={(e) => updateField('contact_title', e.target.value)}
                    placeholder="Your Company Name"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Address</label>
                  <textarea
                    value={settings.contact_address}
                    onChange={(e) => updateField('contact_address', e.target.value)}
                    placeholder="Street address"
                    rows={2}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                  />
                </div>
                <div className="grid gap-4 md:grid-cols-3">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Zip Code</label>
                    <input
                      type="text"
                      value={settings.contact_zip}
                      onChange={(e) => updateField('contact_zip', e.target.value)}
                      placeholder="12345"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">City</label>
                    <input
                      type="text"
                      value={settings.contact_city}
                      onChange={(e) => updateField('contact_city', e.target.value)}
                      placeholder="City"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Country</label>
                    <input
                      type="text"
                      value={settings.contact_country}
                      onChange={(e) => updateField('contact_country', e.target.value)}
                      placeholder="Country"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* VAT Info */}
            <div className="rounded-xl border border-gray-200 p-5 dark:border-gray-700">
              <h3 className="mb-4 text-md font-semibold text-gray-900 dark:text-white">💰 VAT Information</h3>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">VAT Number</label>
                  <input
                    type="text"
                    value={settings.contact_vat}
                    onChange={(e) => updateField('contact_vat', e.target.value)}
                    placeholder="VAT123456789"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">VAT Percentage (%)</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={settings.contact_vat_percent}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === '' || /^\d*\.?\d*$/.test(v)) {
                        updateField('contact_vat_percent', v === '' ? 0 : parseFloat(v) || 0);
                      }
                    }}
                    placeholder="20.00"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                  />
                </div>
              </div>
            </div>

            {/* Invoice Footer */}
            <div className="rounded-xl border border-gray-200 p-5 dark:border-gray-700">
              <h3 className="mb-4 text-md font-semibold text-gray-900 dark:text-white">📄 Invoice Footer</h3>

              {/* English Footer */}
              <div className="mb-4">
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Footer Content (English)</label>
                <textarea
                  value={settings.contact_invoice_footer_en}
                  onChange={(e) => updateField('contact_invoice_footer_en', e.target.value)}
                  placeholder="Thank you for your business. Payment terms: Net 30 days."
                  rows={3}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">This text will appear at the bottom of English invoices</p>
              </div>

              {/* French Footer */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Footer Content (French)</label>
                <textarea
                  value={settings.contact_invoice_footer_fr}
                  onChange={(e) => updateField('contact_invoice_footer_fr', e.target.value)}
                  placeholder="Merci pour votre confiance. Conditions de paiement : Net 30 jours."
                  rows={3}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Ce texte apparaîtra en bas des factures en français</p>
              </div>
            </div>
          </div>
        </div>

        {/* Last Updated */}
        {settings.updated_at && (
          <p className="text-center text-xs text-gray-400 dark:text-gray-500">
            Last updated: {new Date(settings.updated_at).toLocaleString()}
          </p>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </Layout>
  );
};

export default ContactBilling;
